import { extendTheme } from '@chakra-ui/react';

// ─── Custom Chakra Theme for ECOLINKE CRM ──────────────────────────────────
// Premium SaaS design system inspired by Linear, Notion, Stripe, Attio CRM
// Preserves existing purple color palette, only refines usage & adds tokens

const theme = extendTheme({
  // ── Fonts ────────────────────────────────────────────────────────────────
  fonts: {
    heading: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  },

  // ── Global Styles ───────────────────────────────────────────────────────
  styles: {
    global: (props) => ({
      body: {
        fontFamily: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        bg: props.colorMode === 'dark' ? 'gray.900' : '#f8f9fb',
        color: props.colorMode === 'dark' ? 'gray.50' : 'gray.800',
        lineHeight: '1.6',
      },
      '*': {
        borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
      },
      '*::selection': {
        bg: 'purple.100',
        color: 'purple.900',
      },
    }),
  },

  // ── Borders ─────────────────────────────────────────────────────────────
  radii: {
    none: '0',
    sm: '6px',
    base: '8px',
    md: '10px',
    lg: '14px',
    xl: '18px',
    '2xl': '24px',
    '3xl': '32px',
    full: '9999px',
  },

  // ── Shadows (soft, purple-tinted for brand consistency) ─────────────────
  shadows: {
    xs: '0 1px 2px 0 rgba(107, 70, 193, 0.04)',
    sm: '0 1px 3px 0 rgba(107, 70, 193, 0.06), 0 1px 2px -1px rgba(107, 70, 193, 0.06)',
    md: '0 4px 6px -1px rgba(107, 70, 193, 0.07), 0 2px 4px -2px rgba(107, 70, 193, 0.05)',
    lg: '0 10px 15px -3px rgba(107, 70, 193, 0.08), 0 4px 6px -4px rgba(107, 70, 193, 0.05)',
    xl: '0 20px 25px -5px rgba(107, 70, 193, 0.08), 0 8px 10px -6px rgba(107, 70, 193, 0.04)',
    '2xl': '0 25px 50px -12px rgba(107, 70, 193, 0.16)',
    card: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
    'card-hover': '0 10px 40px -10px rgba(107, 70, 193, 0.15), 0 4px 6px -2px rgba(0,0,0,0.05)',
    'card-dark': '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
    'card-dark-hover': '0 10px 40px -10px rgba(107, 70, 193, 0.3), 0 4px 6px -2px rgba(0,0,0,0.2)',
    sidebar: '4px 0 24px -2px rgba(17, 10, 41, 0.2)',
    navbar: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)',
    'input-focus': '0 0 0 3px rgba(128, 90, 213, 0.15)',
  },

  // ── Component Overrides ─────────────────────────────────────────────────
  components: {
    // ── Button ──────────────────────────────────────────────────────────
    Button: {
      baseStyle: {
        fontWeight: '600',
        borderRadius: '10px',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        letterSpacing: '-0.01em',
        _active: { transform: 'scale(0.97)' },
      },
      sizes: {
        sm: {
          h: '34px',
          fontSize: '13px',
          px: '14px',
        },
        md: {
          h: '40px',
          fontSize: '14px',
          px: '18px',
        },
        lg: {
          h: '48px',
          fontSize: '15px',
          px: '24px',
        },
      },
      variants: {
        solid: (props) => ({
          ...(props.colorScheme === 'purple' ? {
            bg: 'linear-gradient(135deg, #7B5EA7 0%, #9F7AEA 100%)',
            color: 'white',
            _hover: {
              bg: 'linear-gradient(135deg, #6B4E97 0%, #8F6ADA 100%)',
              transform: 'translateY(-1px)',
              shadow: 'lg',
              _disabled: { bg: 'purple.400', transform: 'none' },
            },
          } : {}),
        }),
        ghost: {
          _hover: {
            transform: 'translateY(-1px)',
          },
        },
        outline: {
          borderWidth: '1.5px',
          _hover: {
            transform: 'translateY(-1px)',
          },
        },
      },
    },

    // ── Input ───────────────────────────────────────────────────────────
    Input: {
      baseStyle: {
        field: {
          borderRadius: '10px',
          transition: 'all 0.2s ease',
        },
      },
      sizes: {
        md: {
          field: {
            h: '44px',
            fontSize: '14px',
            px: '14px',
            borderRadius: '10px',
          },
        },
      },
      variants: {
        outline: (props) => ({
          field: {
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
            bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
            _hover: {
              borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.300',
            },
            _focus: {
              borderColor: 'purple.400',
              boxShadow: '0 0 0 3px rgba(128, 90, 213, 0.15)',
            },
            _placeholder: {
              color: props.colorMode === 'dark' ? 'gray.500' : 'gray.400',
              fontSize: '14px',
            },
          },
        }),
      },
    },

    // ── Select ──────────────────────────────────────────────────────────
    Select: {
      sizes: {
        md: {
          field: {
            h: '44px',
            fontSize: '14px',
            borderRadius: '10px',
          },
        },
      },
      variants: {
        outline: (props) => ({
          field: {
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
            _hover: { borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.300' },
            _focus: {
              borderColor: 'purple.400',
              boxShadow: '0 0 0 3px rgba(128, 90, 213, 0.15)',
            },
          },
        }),
      },
    },

    // ── Textarea ────────────────────────────────────────────────────────
    Textarea: {
      variants: {
        outline: (props) => ({
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
          borderRadius: '10px',
          fontSize: '14px',
          _hover: { borderColor: props.colorMode === 'dark' ? 'gray.500' : 'gray.300' },
          _focus: {
            borderColor: 'purple.400',
            boxShadow: '0 0 0 3px rgba(128, 90, 213, 0.15)',
          },
        }),
      },
    },

    // ── Card ────────────────────────────────────────────────────────────
    Card: {
      baseStyle: (props) => ({
        container: {
          borderRadius: '14px',
          border: '1px solid',
          borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.100',
          boxShadow: props.colorMode === 'dark' ? 'card-dark' : 'card',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          _hover: {
            boxShadow: props.colorMode === 'dark' ? 'card-dark-hover' : 'card-hover',
          },
        },
      }),
    },

    // ── Table ───────────────────────────────────────────────────────────
    Table: {
      baseStyle: (props) => ({
        th: {
          fontWeight: '700',
          fontSize: '11px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: props.colorMode === 'dark' ? 'gray.400' : 'gray.500',
          bg: props.colorMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(107, 99, 255, 0.05)',
          borderBottom: '1px solid',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.400',
          py: '12px',
          px: '14px',
        },
        td: {
          fontSize: '13px',
          fontWeight: '500',
          borderBottom: '1px solid',
          borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.300',
          py: '12px',
          px: '14px',
        },
        tr: {
          transition: 'all 0.15s ease',
          _hover: {
            bg: props.colorMode === 'dark' ? 'whiteAlpha.50' : 'purple.50',
          },
        },
      }),
      variants: {
        simple: (props) => ({
          th: {
            bg: props.colorMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(107, 99, 255, 0.05)',
            borderBottom: '1px solid',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.400',
          },
          td: {
            borderBottom: '1px solid',
            borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.300',
          },
        }),
        unstyled: (props) => ({
          th: {
            bg: props.colorMode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(107, 99, 255, 0.05)',
            borderBottom: '1px solid',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.400',
          },
          td: {
            borderBottom: '1px solid',
            borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.300',
          },
        }),
      },
    },

    // ── Badge ───────────────────────────────────────────────────────────
    Badge: {
      baseStyle: {
        borderRadius: '6px',
        fontWeight: '600',
        fontSize: '11px',
        letterSpacing: '0.02em',
        px: '8px',
        py: '2px',
        textTransform: 'capitalize',
      },
    },

    // ── Menu ────────────────────────────────────────────────────────────
    Menu: {
      baseStyle: (props) => ({
        list: {
          borderRadius: '14px',
          border: '1px solid',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.100',
          boxShadow: '0 12px 40px -8px rgba(0,0,0,0.12), 0 4px 12px -2px rgba(0,0,0,0.06)',
          py: '6px',
          overflow: 'hidden',
        },
        item: {
          fontSize: '14px',
          py: '10px',
          px: '14px',
          borderRadius: '8px',
          mx: '6px',
          transition: 'all 0.15s ease',
          _hover: {
            bg: props.colorMode === 'dark' ? 'whiteAlpha.100' : 'gray.50',
          },
          _focus: {
            bg: props.colorMode === 'dark' ? 'whiteAlpha.100' : 'gray.50',
          },
        },
      }),
    },

    // ── Modal ───────────────────────────────────────────────────────────
    Modal: {
      baseStyle: (props) => ({
        dialog: {
          borderRadius: '18px',
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.2)',
          border: '1px solid',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.100',
        },
        header: {
          fontSize: '18px',
          fontWeight: '700',
          pt: '24px',
          pb: '12px',
        },
        body: {
          py: '16px',
        },
        footer: {
          pt: '12px',
          pb: '20px',
        },
      }),
    },

    // ── Heading ─────────────────────────────────────────────────────────
    Heading: {
      baseStyle: {
        fontWeight: '700',
        letterSpacing: '-0.02em',
        lineHeight: '1.2',
      },
    },

    // ── Text ────────────────────────────────────────────────────────────
    Text: {
      baseStyle: {
        lineHeight: '1.6',
      },
    },

    // ── FormLabel ───────────────────────────────────────────────────────
    FormLabel: {
      baseStyle: {
        fontSize: '13px',
        fontWeight: '600',
        color: 'gray.600',
        mb: '6px',
        letterSpacing: '-0.01em',
      },
    },

    // ── Tabs ────────────────────────────────────────────────────────────
    Tabs: {
      variants: {
        line: (props) => ({
          tab: {
            fontSize: '14px',
            fontWeight: '500',
            color: props.colorMode === 'dark' ? 'gray.400' : 'gray.500',
            _selected: {
              color: 'purple.500',
              fontWeight: '600',
              borderColor: 'purple.500',
            },
            _hover: {
              color: 'purple.400',
            },
          },
        }),
      },
    },

    // ── Divider ─────────────────────────────────────────────────────────
    Divider: {
      baseStyle: {
        opacity: 0.6,
      },
    },

    // ── Alert ───────────────────────────────────────────────────────────
    Alert: {
      baseStyle: {
        container: {
          borderRadius: '10px',
          fontSize: '14px',
        },
      },
    },

    // ── AlertDialog ─────────────────────────────────────────────────────
    AlertDialog: {
      baseStyle: (props) => ({
        dialog: {
          borderRadius: '18px',
          boxShadow: '0 25px 60px -12px rgba(0,0,0,0.2)',
          border: '1px solid',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.100',
        },
        header: {
          fontSize: '18px',
          fontWeight: '700',
          pt: '24px',
          pb: '8px',
        },
        body: {
          py: '12px',
          fontSize: '14px',
        },
        footer: {
          pt: '8px',
          pb: '20px',
        },
      }),
    },

    // ── Skeleton ────────────────────────────────────────────────────────
    Skeleton: {
      baseStyle: {
        borderRadius: '10px',
      },
    },

    // ── Tooltip ─────────────────────────────────────────────────────────
    Tooltip: {
      baseStyle: {
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '500',
        px: '10px',
        py: '6px',
        boxShadow: 'lg',
      },
    },

    // ── Drawer ──────────────────────────────────────────────────────────
    Drawer: {
      baseStyle: {
        dialog: {
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
        },
      },
    },

    // ── Switch ──────────────────────────────────────────────────────────
    Switch: {
      baseStyle: {
        track: {
          transition: 'all 0.2s ease',
        },
      },
    },

    // ── Checkbox ────────────────────────────────────────────────────────
    Checkbox: {
      baseStyle: {
        control: {
          borderRadius: '5px',
          transition: 'all 0.15s ease',
        },
        label: {
          fontSize: '14px',
        },
      },
    },

    // ── Breadcrumb ──────────────────────────────────────────────────────
    Breadcrumb: {
      baseStyle: {
        link: {
          fontSize: '14px',
          fontWeight: '500',
          color: 'gray.400',
          letterSpacing: '-0.01em',
          transition: 'color 0.15s ease',
          _hover: {
            textDecoration: 'none',
            color: 'purple.400',
          },
        },
        separator: {
          color: 'gray.300',
          mx: '8px',
        },
      },
    },

    // ── Tag ─────────────────────────────────────────────────────────────
    Tag: {
      baseStyle: {
        container: {
          borderRadius: '8px',
          fontWeight: '600',
          fontSize: '12px',
        },
      },
    },

    // ── Stat ────────────────────────────────────────────────────────────
    Stat: {
      baseStyle: {
        label: {
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'gray.500',
        },
        number: {
          fontSize: '24px',
          fontWeight: '800',
          letterSpacing: '-0.02em',
        },
        helpText: {
          fontSize: '12px',
        },
      },
    },

    // ── Popover ─────────────────────────────────────────────────────────
    Popover: {
      baseStyle: {
        content: {
          borderRadius: '14px',
          boxShadow: '0 12px 40px -8px rgba(0,0,0,0.12), 0 4px 12px -2px rgba(0,0,0,0.06)',
          border: '1px solid',
          borderColor: 'gray.100',
          _dark: {
            borderColor: 'gray.600',
          },
        },
      },
    },

    // ── NumberInput ─────────────────────────────────────────────────────
    NumberInput: {
      sizes: {
        md: {
          field: {
            h: '44px',
            fontSize: '14px',
            borderRadius: '10px',
          },
        },
      },
    },

    // ── Progress ────────────────────────────────────────────────────────
    Progress: {
      baseStyle: {
        track: {
          borderRadius: '10px',
        },
        filledTrack: {
          borderRadius: '10px',
          transition: 'width 0.4s ease',
        },
      },
    },
  },

  // ── Transition defaults ─────────────────────────────────────────────────
  transition: {
    property: {
      common: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
    },
    easing: {
      'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    duration: {
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
    },
  },
});

export default theme;
